import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { Undefinable } from '@symfa-inc/providence-types';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { arraySort } from '@core/utils/methods';
import { SectorEquipmentType } from '@models/enums';
import {
  ColumnSelectEquipment,
  EquipmentTypeData,
  ProcessEnvMap as KeyString,
  SectorEquipment,
  SectorEquipmentType as SectorEquipmentTypeInterface,
} from '@models/interfaces';
import store from '@store/index';

interface ColumnsProps<T = any> {
  data: T;
  equipments: ColumnSelectEquipment[];
  label: string;
  type: SectorEquipmentType;
}

const Columns: FC<ColumnsProps> = ({
  data,
  equipments,
  label,
  type,
}: PropsWithChildren<ColumnsProps>) => {
  return (
    <div className="prov-equipment-select">
      {equipments.map(
        ({ id: equipmentId, quantity }: ColumnSelectEquipment) => {
          const item = data.find(
            (equipment: KeyString) => equipment.id === equipmentId,
          );
          const id = item?.id;
          const key = `${type}-${id}-${equipmentId}`;

          return (
            item && (
              <div className="prov-equipment-select__item" key={key}>
                {item?.[label]}
                {quantity > 1 ? ` (${quantity})` : ''}
              </div>
            )
          );
        },
      )}
    </div>
  );
};

export const getPermittingSectorEquipmentsColumns = (
  sectorType: SectorEquipmentTypeInterface[],
  type: SectorEquipmentType,
): ColumnsType => {
  const {
    equipment: { equipmentTypes, sectorsEquipmentsByTypes },
  } = store.getState();

  const getEquipmentTypeName = (equipmentType: string): Undefinable<string> =>
    equipmentTypes?.find(
      (equipment: EquipmentTypeData) => equipment.id === equipmentType,
    )?.name;

  return [
    {
      title: 'Position',
      key: 'position',
      dataIndex: 'position',
      defaultSortOrder: 'ascend',
      width: 40,
    },
    ...arraySort(
      sectorType.map((dataItem: SectorEquipmentTypeInterface) => {
        return {
          title: getEquipmentTypeName(dataItem.equipmentType),
          key: getEquipmentTypeName(dataItem.equipmentType),
          className: 'equipment-model-number',
          width: 200,
          render: (_: unknown, __: unknown, index: number): ReactNode => {
            const position = index + 1;

            const equipments = dataItem?.equipments
              ?.filter(
                (equipment: SectorEquipment) =>
                  equipment.position === position && equipment.equipment,
              )
              ?.map(
                ({
                  equipment: equipmentId,
                  quantity,
                  manufacturer,
                }: SectorEquipment) => ({
                  id: equipmentId,
                  quantity,
                  manufacturer,
                }),
              );

            const currentTypeEquipments =
              sectorsEquipmentsByTypes[dataItem.equipmentType] || [];

            return (
              <Columns
                data={currentTypeEquipments}
                equipments={equipments}
                label="modelNumber"
                type={type}
              />
            );
          },
        };
      }) as ColumnType<unknown>[],
      'ASC',
      'title',
    ),
  ];
};
